import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell } from "@/components/site/PageShell";
import { ServicePage } from "@/components/site/ServicePage";
import heroImg from "@/assets/portfolio-3.jpg";
import heroImgWebp from "@/assets/portfolio-3.webp";
import { seoMeta, canonicalLink, robotsMeta } from "@/lib/seo";
import { contenido } from "@/content/pages/eventos-corporativos";
import { vistaServicio } from "@/lib/page-content/vista";
import type { ServiceSchemaData } from "@/lib/seo-schema-apply";
import { datosHeadServicio, jsonLdServicio } from "@/lib/jsonld-servicios";

const DEFAULT_TITLE = "Agencia de Eventos Corporativos en Lima | Improve Yourself";
const DEFAULT_DESCRIPTION =
  "Agencia y productora de eventos corporativos en Lima: lanzamientos, kick-offs, convenciones y aniversarios. Local, AV, catering, branding y ejecución en sitio.";

const props = {
  slug: "/eventos-corporativos" as const,
  heroImg,
  heroImgWebp,
  heroSrcset:
    "/assets/portfolio-3-400w.webp 400w, /assets/portfolio-3-680w.webp 680w, /assets/portfolio-3-800w.webp 800w",
};
const relatedSlugs = ["/activaciones-btl", "/impulsadoras-promotoras", "/trade-marketing"] as const;

function EventosCorporativosPage() {
  const { siteImageSlots, pageContent } = Route.useRouteContext();
  const vista = vistaServicio(contenido, pageContent);
  const slot = siteImageSlots?.["servicio-eventos-corporativos"];
  return (
    <PageShell>
      <ServicePage
        {...props}
        vista={vista}
        heroImg={slot?.src ?? props.heroImg}
        heroImgWebp={slot?.src ?? props.heroImgWebp}
        heroSrcset={slot?.srcSet ?? props.heroSrcset}
        relatedSlugs={[...relatedSlugs]}
      />
    </PageShell>
  );
}

export const Route = createFileRoute("/eventos-corporativos")({
  component: EventosCorporativosPage,
  // El head() solo lee lo que arma el loader: el loader va en el chunk de la ruta junto con el
  // contenido, y así el contenido no entra al chunk de entrada de todas las páginas.
  loader: ({ context }) => datosHeadServicio(vistaServicio(contenido, context.pageContent)),
  head: ({ match, loaderData }) => {
    const override = match.context.seoOverride;
    const slot = match.context.siteImageSlots?.["servicio-eventos-corporativos"];
    return {
      meta: [
        ...seoMeta({
          title: override?.metaTitle ?? DEFAULT_TITLE,
          description: override?.metaDescription ?? DEFAULT_DESCRIPTION,
          path: "/eventos-corporativos",
          image: override?.ogImage ?? slot?.src ?? heroImg,
        }),
        ...robotsMeta(override?.robotsIndex ?? true, override?.robotsFollow ?? true),
      ],
      links: [
        ...canonicalLink(override?.canonicalPath ?? "/eventos-corporativos"),
        {
          rel: "preload",
          as: "image",
          href: slot?.src ?? heroImgWebp,
          type: "image/webp",
          fetchPriority: "high",
        },
      ],
      scripts: loaderData
        ? jsonLdServicio(
            loaderData,
            "/eventos-corporativos",
            slot?.src ?? heroImg,
            override?.schemaType === "service"
              ? (override.schemaData as Partial<ServiceSchemaData>)
              : null,
          )
        : [],
    };
  },
});
